/**
 * French given-name / surname spelling variants for phonetic highlighting.
 * Keep in sync with api/utils/adminSearch/frenchNameSpellingVariants.js
 *
 * Rule-based model of common silent letters in French anthroponyms (not exhaustive).
 */

const unique = (values) => [...new Set(values.filter(Boolean))];

const normalizeWord = (normalizePhoneticInput, word) => normalizePhoneticInput(word);

const addRemovalVariant = (variants, word, next) => {
  if (next && next !== word && next.length >= 2) variants.add(next);
};

const SILENT_LETTER_REMOVAL_RULES = [
  {
    id: 'mes-prefix',
    remove: (word) => (word.match(/^mes(?=[a-z])/i) ? word.replace(/^mes/i, 'me') : null),
  },
  {
    id: 'des-prefix',
    remove: (word) => (word.match(/^des(?=[a-z])/i) ? word.replace(/^des/i, 'de') : null),
  },
  {
    id: 'les-prefix',
    remove: (word) => (word.match(/^les(?=[a-z])/i) ? word.replace(/^les/i, 'le') : null),
  },
  {
    id: 'vowel-s-consonant',
    remove: (word) => {
      const next = word.replace(/([aeiouy])s([lnmr])/gi, '$1$2');
      return next !== word ? next : null;
    },
  },
  {
    id: 'bapt-cluster',
    remove: (word) => (word.match(/^bapt(?=[a-z])/i) ? word.replace(/^bapt/i, 'bat') : null),
  },
  {
    id: 'sept-cluster',
    remove: (word) => (word.match(/^sept(?=[a-z])/i) ? word.replace(/^sept/i, 'set') : null),
  },
  {
    id: 'pt-after-vowel',
    remove: (word) => {
      const next = word.replace(/([aeiouy])pt(?=[a-z])/gi, '$1t');
      return next !== word ? next : null;
    },
  },
  {
    id: 'final-t-oi',
    remove: (word) =>
      word.length >= 5 && /oit$/i.test(word) ? word.slice(0, -1) : null,
  },
  {
    id: 'final-t-ai',
    remove: (word) =>
      word.length >= 5 && /ait$/i.test(word) ? word.slice(0, -1) : null,
  },
  {
    id: 'final-s-after-vowel',
    remove: (word) => {
      if (word.length < 4) return null;
      if (/^(fils|bus|as)$/i.test(word)) return null;
      if (/[aeiouy]s$/i.test(word)) return word.slice(0, -1);
      return null;
    },
  },
  {
    id: 'final-d-after-vowel',
    remove: (word) => {
      if (word.length < 5) return null;
      if (/[aeiouy]d$/i.test(word)) return word.slice(0, -1);
      return null;
    },
  },
  {
    id: 'final-x-after-vowel',
    remove: (word) => {
      if (word.length < 5) return null;
      if (/[aeiouy]x$/i.test(word)) return word.slice(0, -1);
      return null;
    },
  },
  {
    id: 'final-z-after-vowel',
    remove: (word) => {
      if (word.length < 5) return null;
      if (/[aeiouy]z$/i.test(word)) return word.slice(0, -1);
      return null;
    },
  },
  {
    id: 'final-p-after-vowel',
    remove: (word) => {
      if (word.length < 5) return null;
      if (/[aeiouy]p$/i.test(word)) return word.slice(0, -1);
      return null;
    },
  },
  {
    id: 'final-g-after-vowel',
    remove: (word) => {
      if (word.length < 5) return null;
      if (/[aeiouy]g$/i.test(word)) return word.slice(0, -1);
      return null;
    },
  },
  {
    id: 'final-d-after-r',
    remove: (word) =>
      word.length >= 5 && /rd$/i.test(word) ? word.slice(0, -1) : null,
  },
  {
    id: 'final-t-after-r',
    remove: (word) =>
      word.length >= 5 && /rt$/i.test(word) ? word.slice(0, -1) : null,
  },
  {
    id: 'final-d-after-n',
    remove: (word) =>
      word.length >= 5 && /nd$/i.test(word) ? word.slice(0, -1) : null,
  },
];

const SEARCH_SPELLING_EXPANSION_RULES = [
  {
    id: 'mes-prefix',
    expand: (word) =>
      word.match(/^me(?=[sln])/i) ? word.replace(/^me/i, 'mes') : null,
  },
  {
    id: 'des-prefix',
    expand: (word) =>
      word.match(/^de(?=[sbclm])/i) ? word.replace(/^de/i, 'des') : null,
  },
  {
    id: 'les-prefix',
    expand: (word) =>
      word.match(/^le(?=[sbc])/i) ? word.replace(/^le/i, 'les') : null,
  },
  {
    id: 'bapt-cluster',
    expand: (word) =>
      word.match(/^bat(?=[a-z])/i) && !/^bapt/i.test(word)
        ? word.replace(/^bat/i, 'bapt')
        : null,
  },
  {
    id: 'sept-cluster',
    expand: (word) =>
      word.match(/^set(?=[a-z])/i) && !/^sept/i.test(word)
        ? word.replace(/^set/i, 'sept')
        : null,
  },
  {
    id: 'pt-after-vowel',
    expand: (word) => {
      const next = word.replace(/([aeiouy])t(?=[a-z])/gi, '$1pt');
      return next !== word && !/pt/i.test(word) ? next : null;
    },
  },
  {
    id: 'final-t-oi',
    expand: (word) =>
      word.length >= 4 && /oi$/i.test(word) && !/oit$/i.test(word)
        ? `${word}t`
        : null,
  },
  {
    id: 'final-t-ai',
    expand: (word) =>
      word.length >= 4 && /ai$/i.test(word) && !/ait$/i.test(word)
        ? `${word}t`
        : null,
  },
  {
    id: 'final-s-after-vowel',
    expand: (word) =>
      word.length >= 3 && /[aeiouy]$/i.test(word) && !/[aeiouy]s$/i.test(word)
        ? `${word}s`
        : null,
  },
  {
    id: 'final-d-after-vowel',
    expand: (word) =>
      word.length >= 4 && /[aeiouy]$/i.test(word) && !/[aeiouy]d$/i.test(word)
        ? `${word}d`
        : null,
  },
  {
    id: 'final-x-after-vowel',
    expand: (word) =>
      word.length >= 4 && /[aeiouy]$/i.test(word) && !/[aeiouy]x$/i.test(word)
        ? `${word}x`
        : null,
  },
  {
    id: 'final-z-after-vowel',
    expand: (word) =>
      word.length >= 4 && /[aeiouy]$/i.test(word) && !/[aeiouy]z$/i.test(word)
        ? `${word}z`
        : null,
  },
  {
    id: 'final-p-after-vowel',
    expand: (word) =>
      word.length >= 4 && /[aeiouy]$/i.test(word) && !/[aeiouy]p$/i.test(word)
        ? `${word}p`
        : null,
  },
  {
    id: 'final-g-after-vowel',
    expand: (word) =>
      word.length >= 4 && /[aeiouy]$/i.test(word) && !/[aeiouy]g$/i.test(word)
        ? `${word}g`
        : null,
  },
  {
    id: 'final-d-after-r',
    expand: (word) =>
      word.length >= 5 && /ar$/i.test(word) && !/ard$/i.test(word)
        ? `${word}d`
        : null,
  },
  {
    id: 'final-t-after-r',
    expand: (word) =>
      word.length >= 5 && /er$/i.test(word) && !/ert$/i.test(word)
        ? `${word}t`
        : null,
  },
  {
    id: 'final-d-after-n',
    expand: (word) =>
      word.length >= 5 && /an$/i.test(word) && !/and$/i.test(word)
        ? `${word}d`
        : null,
  },
];

const buildSilentFrenchSpellingVariants = (word, normalizePhoneticInput) => {
  const normalized = normalizeWord(normalizePhoneticInput, word);
  if (!normalized) return [];

  const variants = new Set();
  for (const rule of SILENT_LETTER_REMOVAL_RULES) {
    const next = rule.remove(normalized);
    addRemovalVariant(variants, normalized, next);
  }

  return [...variants];
};

const buildSearchPhoneticExpansions = (word, normalizePhoneticInput) => {
  const normalized = normalizeWord(normalizePhoneticInput, word);
  if (!normalized) return [];

  const variants = new Set();
  for (const rule of SEARCH_SPELLING_EXPANSION_RULES) {
    const next = rule.expand(normalized);
    addRemovalVariant(variants, normalized, next);
  }

  return [...variants];
};

export {
  buildSearchPhoneticExpansions,
  buildSilentFrenchSpellingVariants,
  SEARCH_SPELLING_EXPANSION_RULES,
  SILENT_LETTER_REMOVAL_RULES,
};
